import { createFileRoute } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2 } from "lucide-react";
import { getSettings, updateSettings } from "@/lib/admin.functions";
import { getMaintenanceState } from "@/lib/maintenance.functions";

export const Route = createFileRoute("/_authenticated/admin/maintenance")({
  staticData: { sitemap: false },
  component: MaintenancePage,
});

function MaintenancePage() {
  const qc = useQueryClient();
  const getFn = useServerFn(getSettings);
  const updFn = useServerFn(updateSettings);
  const stateFn = useServerFn(getMaintenanceState);

  const { data, isLoading } = useQuery({ queryKey: ["admin", "settings"], queryFn: () => getFn() });
  const { data: state } = useQuery({ queryKey: ["admin", "maintenance-state"], queryFn: () => stateFn() });

  const [enabled, setEnabled] = useState(false);
  const [message, setMessage] = useState("");
  const [ips, setIps] = useState<string[]>([]);
  const [newIp, setNewIp] = useState("");

  useEffect(() => {
    if (!data) return;
    setEnabled(!!data.maintenance_enabled);
    setMessage(data.maintenance_message ?? "");
    setIps(data.maintenance_allowed_ips ?? []);
  }, [data]);

  const save = useMutation({
    mutationFn: (payload: { maintenance_enabled: boolean; maintenance_message: string; maintenance_allowed_ips: string[] }) =>
      updFn({ data: payload }),
    onSuccess: () => {
      toast.success("Maintenance settings saved");
      qc.invalidateQueries({ queryKey: ["admin", "settings"] });
    },
    onError: (e: Error) => toast.error(e.message),
  });

  const dirty =
    !!data &&
    (enabled !== !!data.maintenance_enabled ||
      message !== (data.maintenance_message ?? "") ||
      JSON.stringify(ips) !== JSON.stringify(data.maintenance_allowed_ips ?? []));

  function addIp(value: string) {
    const v = value.trim();
    if (!v) return;
    if (ips.includes(v)) {
      toast.error("Already on the list");
      return;
    }
    setIps([...ips, v]);
    setNewIp("");
  }

  if (isLoading) {
    return <div className="text-mono-caps text-ink/40">Loading…</div>;
  }

  return (
    <div className="space-y-8 max-w-3xl">
      <div>
        <h1 className="text-2xl">Maintenance</h1>
        <p className="text-sm text-ink/50 mt-1">
          When maintenance mode is on, visitors see a temporary maintenance notice instead of the site.
          The backoffice and the sign-in page always stay reachable.
        </p>
      </div>

      <div className="border border-ink/10 p-4 space-y-4">
        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={enabled}
            onChange={(e) => setEnabled(e.target.checked)}
            className="mt-1 accent-amber h-4 w-4"
          />
          <span>
            <span className="text-sm font-medium">Enable maintenance mode</span>
            <span className="block text-xs text-ink/50">
              {enabled ? "The public site is currently hidden." : "The public site is visible to everyone."}
            </span>
          </span>
        </label>

        <div>
          <div className="text-mono-caps text-ink/60 mb-2">Message shown to visitors</div>
          <textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            rows={3}
            maxLength={2000}
            className="w-full border border-ink/15 bg-transparent px-3 py-2 text-sm"
          />
        </div>

        <div>
          <div className="text-mono-caps text-ink/60 mb-2">Allowed IP addresses</div>
          <p className="text-xs text-ink/50 mb-3">
            These IPs keep full access while maintenance mode is on. You can use a wildcard prefix such as
            {" "}<code className="text-ink/70">83.45.12.*</code>.
          </p>

          <div className="flex gap-2 mb-3">
            <input
              value={newIp}
              onChange={(e) => setNewIp(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  e.preventDefault();
                  addIp(newIp);
                }
              }}
              placeholder="e.g. 83.45.12.99"
              className="flex-1 border border-ink/15 bg-transparent px-3 py-2 text-sm font-mono"
            />
            <button
              onClick={() => addIp(newIp)}
              className="bg-ink text-paper px-3 py-2 text-sm hover:bg-amber transition-colors inline-flex items-center gap-1.5"
            >
              <Plus className="h-4 w-4" /> Add
            </button>
          </div>

          {state?.ip ? (
            <p className="text-xs text-ink/50 mb-3">
              Your current IP is <code className="text-ink/70">{state.ip}</code>
              {!ips.includes(state.ip) && (
                <>
                  {" · "}
                  <button onClick={() => addIp(state.ip)} className="underline hover:text-amber">
                    add it
                  </button>
                </>
              )}
            </p>
          ) : null}

          {ips.length === 0 ? (
            <p className="text-xs text-ink/40">No IPs allowed yet — nobody but the backoffice will see the site.</p>
          ) : (
            <ul className="divide-y divide-ink/8 border border-ink/10">
              {ips.map((ip) => (
                <li key={ip} className="flex items-center justify-between px-3 py-2 text-sm">
                  <span className="font-mono">{ip}</span>
                  <button
                    onClick={() => setIps(ips.filter((x) => x !== ip))}
                    title="Remove"
                    className="text-ink/40 hover:text-red-600 transition-colors"
                  >
                    <Trash2 className="h-4 w-4" />
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex justify-end">
          <button
            onClick={() =>
              save.mutate({
                maintenance_enabled: enabled,
                maintenance_message: message,
                maintenance_allowed_ips: ips,
              })
            }
            disabled={save.isPending || !dirty}
            className="bg-ink text-paper px-4 py-2 text-sm hover:bg-amber transition-colors disabled:opacity-30 disabled:cursor-not-allowed"
          >
            {save.isPending ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
